// src/components/RegParticipantsStep.js
// Step 2 — participants.
//   mode="company": add one or more participants (email/phone optional).
//   mode="direct" : exactly ONE participant; their EMAIL is required and is
//                   verified by OTP right here before submission.
// Each participant is identified by National ID (CIN) or passport; existing
// people are reused (looked up on blur).
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import eventApi from "../config/eventApi";

const emptyParticipant = () => ({
  _key: Math.random().toString(36).slice(2),
  id: null,
  civilite: "",
  nom: "",
  prenom: "",
  nom_ar: "",
  prenom_ar: "",
  numero_carte_identite: "",
  numero_passeport: "",
  email: "",
  telephone: "",
  fonction: "",
  _reused: false,
});

export default function RegParticipantsStep({
  participants, setParticipants, onBack, onSubmit, submitting, isPaid, prix,
  idEvent, mode = "company", isUpdate = false, requireOtp = true,
  tauxTva = 0, paymentMethods = [], payment = {}, setPayment = () => {},
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const isDirect = mode === "direct";

  // Payment proof -> base64 (sent inline in the submit payload).
  const onProofChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) { setPayment((p) => ({ ...p, preuve_base64: null, preuve_filename: "" })); return; }
    if (file.size > 5 * 1024 * 1024) { toast.warning(t("reg.proof_too_big", "Fichier trop volumineux (max 5 Mo)")); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = () => setPayment((p) => ({ ...p, preuve_base64: reader.result, preuve_filename: file.name }));
    reader.readAsDataURL(file);
  };

  const [lookingIdx, setLookingIdx] = useState(null);

  // Direct-mode OTP state (verifies participants[0].email)
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [otpVerified, setOtpVerified] = useState(false);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);

  // Direct mode: lock identity fields when the participant already exists,
  // and remember the verified email so keeping it skips a new OTP.
  const [directLocked, setDirectLocked] = useState(false);
  const [directOriginalEmail, setDirectOriginalEmail] = useState("");
  const [directEmailPreVerified, setDirectEmailPreVerified] = useState(false);

  // When the organizer disabled OTP, the direct-mode e-mail needs no code.
  useEffect(() => {
    if (isDirect && !requireOtp) setOtpVerified(true);
  }, [isDirect, requireOtp]);

  const update = (idx, k, v) => {
    setParticipants((prev) => prev.map((p, i) => (i === idx ? { ...p, [k]: v } : p)));
    // changing the direct email re-arms OTP unless it matches a pre-verified one
    if (isDirect && idx === 0 && k === "email") {
      if (!requireOtp) {
        setOtpVerified(true);
      } else if (directEmailPreVerified) {
        const changed = (v || "").toLowerCase() !== directOriginalEmail;
        setOtpVerified(!changed);
        if (changed) { setOtpSent(false); setOtpCode(""); }
      } else {
        setOtpVerified(false);
        setOtpSent(false);
      }
    }
  };

  const addRow = () => setParticipants((prev) => [...prev, emptyParticipant()]);
  const removeRow = (idx) =>
    setParticipants((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== idx) : prev));

  const handleLookup = async (idx) => {
    const p = participants[idx];
    const cin = (p.numero_carte_identite || "").trim();
    const passport = (p.numero_passeport || "").trim();
    if (!cin && !passport) return;

    setLookingIdx(idx);
    try {
      const { data } = await eventApi.lookupParticipant(
        { numero_carte_identite: cin, numero_passeport: passport }, lang
      );
      if (data.exists) {
        setParticipants((prev) =>
          prev.map((row, i) => (i === idx ? {
            ...row,
            id: data.participant.id,
            civilite: data.participant.civilite || row.civilite,
            nom: data.participant.nom || row.nom,
            prenom: data.participant.prenom || row.prenom,
            nom_ar: data.participant.nom_ar || row.nom_ar,
            prenom_ar: data.participant.prenom_ar || row.prenom_ar,
            email: data.participant.email || row.email,
            telephone: data.participant.telephone || row.telephone,
            fonction: data.participant.fonction || row.fonction,
            _reused: true,
          } : row))
        );

        // Direct mode → lock identity fields, handle pre-verified email.
        if (isDirect && idx === 0) {
          setDirectLocked(true);
          const mail = (data.participant.email || "").toLowerCase();
          setDirectOriginalEmail(mail);
          if (data.email_verified && mail) {
            setDirectEmailPreVerified(true);
            setOtpVerified(true);
            toast.success(t("reg.participant_found_verified", "Profil trouvé — e-mail déjà vérifié"));
          } else {
            setDirectEmailPreVerified(false);
            setOtpVerified(false);
            toast.success(t("reg.participant_found", "Participant déjà connu — profil réutilisé"));
          }
        } else {
          toast.success(t("reg.participant_found", "Participant déjà connu — profil réutilisé"));
        }
      }
    } catch (e) { /* lookup is best-effort */ } finally {
      setLookingIdx(null);
    }
  };

  // ── Direct-mode OTP ──
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const directEmail = (participants[0]?.email || "").trim();

  const handleSendOtp = async () => {
    if (!emailRegex.test(directEmail)) {
      toast.warning(t("reg.email_required", "Veuillez saisir un email valide"));
      return;
    }
    setSending(true);
    try {
      await eventApi.sendOtp({ email: directEmail, id_event: idEvent }, lang);
      setOtpSent(true);
      toast.success(t("reg.otp_sent", "Code de vérification envoyé à votre email"));
    } catch (e) {
      toast.error(e?.response?.data?.message || t("reg.otp_send_failed", "Échec de l'envoi du code"));
    } finally { setSending(false); }
  };

  const handleVerifyOtp = async () => {
    if (!otpCode) return;
    setVerifying(true);
    try {
      await eventApi.verifyOtp({ email: directEmail, code: otpCode }, lang);
      setOtpVerified(true);
      toast.success(t("reg.email_verified", "Email vérifié avec succès"));
    } catch (e) {
      toast.error(e?.response?.data?.message || t("reg.otp_invalid", "Code incorrect"));
    } finally { setVerifying(false); }
  };

  const validateBeforeSubmit = () => {
    for (let i = 0; i < participants.length; i++) {
      const p = participants[i];
      if (!p.nom || !p.prenom) {
        toast.warning(t("reg.name_required", `Participant #${i + 1} : nom et prénom obligatoires`));
        return false;
      }
      const hasId = (p.numero_carte_identite || "").trim() || (p.numero_passeport || "").trim();
      if (!hasId) {
        toast.warning(t("reg.id_required", `Participant #${i + 1} : carte d'identité ou passeport obligatoire`));
        return false;
      }
    }
    if (isDirect) {
      if (!emailRegex.test(directEmail)) {
        toast.warning(t("reg.email_required", "Email du participant obligatoire"));
        return false;
      }
      if (requireOtp && !otpVerified) {
        toast.warning(t("reg.verify_email_first", "Veuillez vérifier votre email (code OTP) avant de continuer"));
        return false;
      }
    }
    if (isPaid && !(payment && payment.mode_paiement)) {
      toast.warning(t("reg.choose_payment", "Veuillez choisir un mode de paiement"));
      return false;
    }
    return true;
  };

  const handleSubmit = () => { if (validateBeforeSubmit()) onSubmit(); };

  const visible = isDirect ? participants.slice(0, 1) : participants;

  return (
    <div className="card shadow-sm mb-4">
      <div className="card-header bg-white d-flex justify-content-between align-items-center">
        <h5 className="mb-0">
          <i className="bi bi-people me-2 text-primary"></i>
          {isDirect ? t("reg.your_info", "Vos informations") : t("reg.participants_step", "Participants")}
          {!isDirect && <span className="badge bg-primary ms-2">{participants.length}</span>}
        </h5>
        {!isDirect && (
          <button type="button" className="btn btn-sm btn-success" onClick={addRow}>
            <i className="bi bi-plus-lg me-1"></i>{t("reg.add_participant", "Ajouter un participant")}
          </button>
        )}
      </div>
      <div className="card-body">
        {isPaid && (
          <div className="reg-payment-box border rounded p-3 mb-3 bg-light">
            <div className="d-flex align-items-center mb-2">
              <i className="bi bi-cash-coin me-2 text-primary fs-5"></i>
              <strong>{t("reg.payment", "Paiement")}</strong>
            </div>
            {(() => {
              const n = visible.length;
              const rate = parseFloat(tauxTva) || 0;
              const ht = (prix || 0) * n;
              const tva = ht * rate / 100;
              const ttc = ht + tva;
              return (
                <div className="small mb-3">
                  <div>{t("reg.unit_price", "Prix unitaire")}: {(prix || 0).toFixed(2)} DZD × {n}</div>
                  <div>{t("reg.subtotal", "Montant HT")}: {ht.toFixed(2)} DZD</div>
                  {rate > 0 && <div>{t("reg.vat", "TVA")} ({rate.toFixed(2)}%): {tva.toFixed(2)} DZD</div>}
                  <div className="fs-6 fw-bold text-primary">{t("reg.total_ttc", "Total à payer (TTC)")}: {ttc.toFixed(2)} DZD</div>
                </div>
              );
            })()}

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label small"><span className="text-danger">*</span> {t("reg.payment_method", "Comment souhaitez-vous payer ?")}</label>
                <select className="form-select" value={(payment && payment.mode_paiement) || ""}
                  onChange={(e) => setPayment((pp) => ({ ...pp, mode_paiement: e.target.value }))}>
                  <option value="">{t("reg.choose", "— Choisir —")}</option>
                  {(paymentMethods || []).map((m) => (
                    <option key={m.value} value={m.value}>{m.label}</option>
                  ))}
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label small">{t("reg.payment_proof", "Preuve de paiement (optionnel)")}</label>
                <input type="file" className="form-control" accept="application/pdf,image/*" onChange={onProofChange} />
                {payment && payment.preuve_filename && (
                  <div className="small text-success mt-1"><i className="bi bi-paperclip me-1"></i>{payment.preuve_filename}</div>
                )}
              </div>
            </div>
            <div className="small text-muted mt-2">
              <i className="bi bi-info-circle me-1"></i>
              {t("reg.payment_pending_note", "Vous pouvez vous inscrire sans payer maintenant : votre paiement sera vérifié et confirmé par l'organisateur.")}
            </div>
          </div>
        )}

        {visible.map((p, idx) => (
          <div key={p._key} className="border rounded p-3 mb-3 position-relative bg-light">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold">
                <i className="bi bi-person-badge me-1"></i>
                {isDirect ? t("reg.participant", "Participant") : `${t("reg.participant", "Participant")} #${idx + 1}`}
                {p._reused && (
                  <span className="badge bg-info ms-2"><i className="bi bi-recycle me-1"></i>{t("reg.reused", "Profil réutilisé")}</span>
                )}
              </span>
              {!isDirect && participants.length > 1 && (
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removeRow(idx)}>
                  <i className="bi bi-trash"></i>
                </button>
              )}
            </div>

            <div className="row g-3">
              <div className="col-md-6">
                <label className="form-label small">{t("reg.cin", "N° Carte d'identité")}</label>
                <div className="input-group">
                  <input type="text" className="form-control" value={p.numero_carte_identite}
                    onChange={(e) => update(idx, "numero_carte_identite", e.target.value)} onBlur={() => handleLookup(idx)} />
                  {lookingIdx === idx && <span className="input-group-text"><i className="bi bi-arrow-repeat spinning"></i></span>}
                </div>
              </div>
              <div className="col-md-6">
                <label className="form-label small">{t("reg.passport", "N° Passeport")}</label>
                <input type="text" className="form-control" value={p.numero_passeport}
                  onChange={(e) => update(idx, "numero_passeport", e.target.value)} onBlur={() => handleLookup(idx)} />
              </div>

              <div className="col-md-2">
                <label className="form-label small">{t("reg.civilite", "Civilité")}</label>
                <select className="form-select" value={p.civilite} onChange={(e) => update(idx, "civilite", e.target.value)} disabled={isDirect && directLocked}>
                  <option value="">-</option>
                  <option value="M.">M.</option>
                  <option value="Mme">Mme</option>
                  <option value="Mlle">Mlle</option>
                  <option value="Dr.">Dr.</option>
                  <option value="Pr.">Pr.</option>
                </select>
              </div>
              <div className="col-md-5">
                <label className="form-label small"><span className="text-danger">*</span> {t("reg.nom", "Nom")}</label>
                <input type="text" className="form-control" value={p.nom} onChange={(e) => update(idx, "nom", e.target.value)} disabled={isDirect && directLocked} />
              </div>
              <div className="col-md-5">
                <label className="form-label small"><span className="text-danger">*</span> {t("reg.prenom", "Prénom")}</label>
                <input type="text" className="form-control" value={p.prenom} onChange={(e) => update(idx, "prenom", e.target.value)} disabled={isDirect && directLocked} />
              </div>

              {/* Email: optional in company mode; required + OTP in direct mode.
                  Editable even after verification so the user can change it (re-OTP). */}
              <div className="col-md-4">
                <label className="form-label small">
                  {isDirect ? <><span className="text-danger">*</span> {t("reg.email", "Email")}</> : t("reg.email_opt", "Email (optionnel)")}
                  {isDirect && directEmailPreVerified && otpVerified && (
                    <span className="badge bg-success ms-2"><i className="bi bi-check-circle me-1"></i>{t("reg.verified", "Vérifié")}</span>
                  )}
                </label>
                <input type="email" className={`form-control ${isDirect && otpVerified ? "is-valid" : ""}`} value={p.email}
                  onChange={(e) => update(idx, "email", e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label small">{t("reg.phone_opt", "Téléphone (optionnel)")}</label>
                <input type="text" className="form-control" value={p.telephone} onChange={(e) => update(idx, "telephone", e.target.value)} />
              </div>
              <div className="col-md-4">
                <label className="form-label small">{t("reg.fonction", "Fonction")}</label>
                <input type="text" className="form-control" value={p.fonction} onChange={(e) => update(idx, "fonction", e.target.value)} />
              </div>
            </div>

            {/* Direct-mode OTP block */}
            {isDirect && requireOtp && (
              <div className="mt-3">
                {!otpVerified ? (
                  <>
                    <button type="button" className="btn btn-warning" onClick={handleSendOtp} disabled={sending}>
                      {sending
                        ? <><i className="bi bi-arrow-repeat spinning me-1"></i>{t("reg.sending", "Envoi...")}</>
                        : <><i className="bi bi-shield-lock me-1"></i>{otpSent ? t("reg.resend_otp", "Renvoyer le code") : t("reg.send_otp", "Vérifier mon email")}</>}
                    </button>
                    {otpSent && (
                      <div className="alert alert-light border d-flex flex-wrap align-items-end gap-2 mt-2 mb-0">
                        <div className="flex-grow-1">
                          <label className="form-label mb-1 small">{t("reg.enter_otp", "Saisissez le code reçu par email")}</label>
                          <input type="text" inputMode="numeric" maxLength={6}
                            className="form-control form-control-lg text-center" style={{ letterSpacing: "0.5em", maxWidth: 220 }}
                            value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))} placeholder="••••••" />
                        </div>
                        <button type="button" className="btn btn-success btn-lg" onClick={handleVerifyOtp} disabled={verifying || otpCode.length < 6}>
                          {verifying ? <><i className="bi bi-arrow-repeat spinning me-1"></i>{t("reg.verifying", "Vérification...")}</>
                            : <><i className="bi bi-check2 me-1"></i>{t("reg.verify", "Valider")}</>}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-success fw-semibold"><i className="bi bi-check-circle-fill me-1"></i>{t("reg.email_verified", "Email vérifié")}</div>
                )}
              </div>
            )}
          </div>
        ))}

        <div className="d-flex justify-content-between mt-4">
          {onBack ? (
            <button type="button" className="btn btn-outline-secondary btn-lg" onClick={onBack}>
              <i className="bi bi-arrow-left me-1"></i>{t("reg.back", "Retour")}
            </button>
          ) : <span />}
          <button type="button" className="btn btn-success btn-lg px-4" onClick={handleSubmit} disabled={submitting}>
            {submitting
              ? <><i className="bi bi-arrow-repeat spinning me-1"></i>{t("reg.submitting", "Envoi...")}</>
              : isUpdate
                ? <><i className="bi bi-save me-1"></i>{t("reg.save_changes", "Enregistrer les modifications")}</>
                : isPaid
                  ? <><i className="bi bi-credit-card me-1"></i>{t("reg.submit_pay", "Confirmer et payer")}</>
                  : <><i className="bi bi-check2-circle me-1"></i>{t("reg.submit", "Confirmer l'inscription")}</>}
          </button>
        </div>
      </div>
    </div>
  );
}